"use client";

import { useMemo } from "react";
import { useEntityStore } from "@/stores/entity.store";
import { getDefaultField } from "@/lib/utils";
import type { Entity, EntityRelated } from "@/services/entity.service";
import type { MediaUsageRelation } from "@/services/pylo-media.service";

const MEDIA_ENTITY = "PyloMedia";

const isMultiFromOwner = (type: string): boolean => type === "OneToMany" || type === "ManyToMany";

/**
 * Relations the media library can traverse. Two shapes coexist:
 *
 * - `inverse`: `Standort.icon → PyloMedia` where the owner named
 *   `target_field_name` surfaces on `PyloMedia` as e.g. `Standort_of_icon`.
 *   Filterable and queryable through `PyloMedia.<fieldName>`.
 * - `owner`: same shape without `target_field_name` — no GraphQL field on
 *   `PyloMedia` exists for it, so usage is resolved by querying the owner
 *   directly. Only picked up here so downstream can fan out those queries.
 *
 * Reads `entityMapForNames` directly: the store's getters are created once and
 * keep the same identity forever, so memoizing on them would freeze the result
 * at the empty first render.
 */
export const usePyloMediaRelations = (): Array<MediaUsageRelation> => {
  const entityMap = useEntityStore((state) => state.entityMapForNames);

  return useMemo(() => {
    const media = entityMap.get(MEDIA_ENTITY) as
      | (Entity & { combined_relations?: Array<EntityRelated> })
      | undefined;
    if (!media) return [];

    // System entities (PyloImportJob, PyloExportJob) are in the generated schema
    // but have no Eloquent relation method on PyloMedia, so querying one throws
    // "Call to undefined method" and takes the whole request with it.
    const isUsableEntity = (name: string): boolean =>
      !!name && !entityMap.get(name)?.is_system_entity;

    const inverse: Array<MediaUsageRelation> = (media.combined_relations ?? [])
      .filter((relation) => !!relation.field_name && !!relation.target_entity?.data?.name)
      .filter((relation) => isUsableEntity(relation.target_entity.data.name))
      .map((relation) => {
        const entityName = relation.target_entity.data.name;
        const fields = entityMap.get(entityName)?.entity_fields?.data ?? [];
        return {
          source: "inverse" as const,
          fieldName: relation.field_name,
          entityName,
          displayField: getDefaultField(entityName, fields) ?? "id",
          isList: relation.type === "OneToMany" || relation.type === "ManyToMany",
        };
      });

    // One-way relations from other entities to PyloMedia (no target_field_name).
    // `entity_related` holds them un-normalized; the store only merges those
    // with a target_field_name into `combined_relations`.
    const owner: Array<MediaUsageRelation> = (media.entity_related?.data ?? [])
      .filter((relation) => !relation.target_field_name)
      .filter((relation) => !!relation.field_name && !!relation.entity?.data?.name)
      .filter((relation) => isUsableEntity(relation.entity.data.name))
      .map((relation) => {
        const entityName = relation.entity.data.name;
        const fields = entityMap.get(entityName)?.entity_fields?.data ?? [];
        return {
          source: "owner" as const,
          fieldName: relation.field_name,
          entityName,
          displayField: getDefaultField(entityName, fields) ?? "id",
          isList: isMultiFromOwner(relation.type),
        };
      });

    return [...inverse, ...owner].sort((a, b) => a.entityName.localeCompare(b.entityName));
  }, [entityMap]);
};
